/**
 * Gedeelde Boek-een-meeting CTA component voor alle Tool Hub tools.
 * Default URL: https://zynq.org/boeken/
 *
 * Gebruik:
 *   <BookingCta config={config} variant="emerald" />
 *
 * `config` is een object met (allemaal optioneel):
 *   - booking_cta_enabled (bool, default true)
 *   - booking_cta_label (string)
 *   - booking_cta_subtitle (string)
 *   - booking_url (string, default https://zynq.org/boeken/)
 */
import { CalendarCheck, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export const DEFAULT_BOOKING_URL = 'https://zynq.org/boeken/';

const VARIANTS = {
  orange: {
    cardClass: 'border-orange-100 bg-gradient-to-br from-orange-50 to-amber-50/40',
    iconBg: 'bg-orange-200/70',
    iconColor: 'text-orange-700',
    titleColor: 'text-orange-900',
    btnClass: 'bg-orange-600 hover:bg-orange-700 text-white',
  },
  emerald: {
    cardClass: 'border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50/40',
    iconBg: 'bg-emerald-200/70',
    iconColor: 'text-emerald-700',
    titleColor: 'text-emerald-900',
    btnClass: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  },
  violet: {
    cardClass: 'border-violet-100 bg-gradient-to-br from-violet-50 to-purple-50/40',
    iconBg: 'bg-violet-200/70',
    iconColor: 'text-violet-700',
    titleColor: 'text-violet-900',
    btnClass: 'bg-violet-600 hover:bg-violet-700 text-white',
  },
};

export function resolveBookingConfig(config) {
  if (!config) {
    return {
      enabled: true,
      label: 'Plan een gesprek',
      subtitle: 'Bespreek je resultaten 1-op-1 met een Zynq-expert.',
      url: DEFAULT_BOOKING_URL,
    };
  }
  return {
    enabled: config.booking_cta_enabled !== false,
    label: (config.booking_cta_label || '').trim() || 'Plan een gesprek',
    subtitle:
      (config.booking_cta_subtitle || '').trim() ||
      'Bespreek je resultaten 1-op-1 met een Zynq-expert.',
    url: (config.booking_url || '').trim() || DEFAULT_BOOKING_URL,
  };
}

export default function BookingCta({ config, variant = 'orange', compact = false, className = '' }) {
  const cta = resolveBookingConfig(config);
  if (!cta.enabled) return null;
  const v = VARIANTS[variant] || VARIANTS.orange;

  if (compact) {
    return (
      <a
        href={cta.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="booking-cta-link"
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-md ${v.btnClass} text-sm font-semibold shadow-sm ${className}`}
      >
        <CalendarCheck className="w-4 h-4" /> {cta.label}
      </a>
    );
  }

  return (
    <Card className={`shadow-sm ${v.cardClass} ${className}`} data-testid="booking-cta-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-full ${v.iconBg} flex items-center justify-center flex-shrink-0`}>
            <CalendarCheck className={`w-6 h-6 ${v.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-bold ${v.titleColor} text-base mb-1`}>{cta.label}</h3>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">{cta.subtitle}</p>
            <Button
              asChild
              data-testid="booking-cta-btn"
              className={v.btnClass}
            >
              <a href={cta.url} target="_blank" rel="noopener noreferrer">
                <CalendarCheck className="w-4 h-4 mr-2" /> {cta.label}
                <ExternalLink className="w-3 h-3 ml-2 opacity-80" />
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
